import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import {
  PlusIcon,
  CircleStackIcon,
  ServerIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  TableCellsIcon,
  BuildingOffice2Icon,
  ClockIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const RssIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="6.18" cy="17.82" r="2.18"/>
    <path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z"/>
  </svg>
);

// Complete source type configurations
const SOURCE_TYPE_CONFIG = {
  csv: {
    name: 'CSV File',
    description: 'Upload a CSV file',
    icon: DocumentTextIcon,
    color: 'text-primary-500'
  },
  excel: {
    name: 'Excel File',
    description: 'Upload an Excel file',
    icon: TableCellsIcon,
    color: 'text-green-500'
  },
  api: {
    name: 'REST API',
    description: 'Connect to an API',
    icon: GlobeAltIcon,
    color: 'text-amber-500'
  },
  postgresql: {
    name: 'PostgreSQL',
    description: 'PostgreSQL database',
    icon: ServerIcon,
    color: 'text-indigo-500'
  },
  mysql: {
    name: 'MySQL',
    description: 'MySQL database',
    icon: ServerIcon,
    color: 'text-orange-500'
  },
  mongodb: {
    name: 'MongoDB',
    description: 'MongoDB database',
    icon: CircleStackIcon,
    color: 'text-emerald-500'
  },
  googlesheets: {
    name: 'Google Sheets',
    description: 'Google Sheets',
    icon: TableCellsIcon,
    color: 'text-green-600'
  },
  googleanalytics: {
    name: 'Google Analytics',
    description: 'Google Analytics 4',
    icon: ChartBarIcon,
    color: 'text-orange-500'
  },
  webscraper: {
    name: 'Web Scraper',
    description: 'AI-powered web scraping',
    icon: MagnifyingGlassIcon,
    color: 'text-purple-500'
  },
  airesearch: {
    name: 'AI Research',
    description: 'AI-powered web research',
    icon: SparklesIcon,
    color: 'text-cyan-500'
  },
  rss: {
    name: 'RSS Feed',
    description: 'AI-enriched RSS feed',
    icon: RssIcon,
    color: 'text-orange-500'
  }
};

const sourceTypeIcons = {
  csv: DocumentTextIcon,
  excel: TableCellsIcon,
  api: GlobeAltIcon,
  postgresql: ServerIcon,
  mysql: ServerIcon,
  mongodb: CircleStackIcon,
  googlesheets: TableCellsIcon,
  googleanalytics: ChartBarIcon,
  webscraper: MagnifyingGlassIcon,
  airesearch: SparklesIcon,
  rss: RssIcon
};

const statusColors = {
  ready: 'badge-success',
  connected: 'badge-primary',
  pending: 'badge-warning',
  importing: 'badge-warning',
  error: 'badge-danger'
};

// Get recently used source types from data sources
const getRecentlyUsedTypes = (dataSources) => {
  if (!dataSources || dataSources.length === 0) {
    return [];
  }
  
  // Group by type and track the most recent usage
  const typeUsage = {};
  dataSources.forEach(source => {
    const type = source.type;
    const lastUsed = new Date(source.stats?.lastImportedAt || source.updatedAt || source.createdAt);
    
    if (!typeUsage[type] || lastUsed > typeUsage[type].lastUsed) {
      typeUsage[type] = {
        type,
        lastUsed,
        count: (typeUsage[type]?.count || 0) + 1
      };
    } else {
      typeUsage[type].count += 1;
    }
  });
  
  // Sort by last used date and return top 4
  return Object.values(typeUsage)
    .sort((a, b) => b.lastUsed - a.lastUsed)
    .slice(0, 4);
};

export default function DataSourceList() {
  const navigate = useNavigate();
  const { isSuperUser, getCurrentCompanyId, getCurrentCompany } = useAuthStore();
  
  const currentCompanyId = getCurrentCompanyId();
  const currentCompany = getCurrentCompany();
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dataSources', currentCompanyId],
    queryFn: () => apiHelpers.getDataSources({ companyId: currentCompanyId }),
    enabled: !!currentCompanyId || !isSuperUser()
  });
  
  const dataSources = data?.data?.data || [];
  
  // Show company selection prompt for super users without a selected company
  if (isSuperUser() && !currentCompanyId) {
    return (
      <div className="animate-fade-in">
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center">
            <BuildingOffice2Icon className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            Select a Company to View Data Sources
          </h3>
          <p className="text-surface-500 dark:text-surface-400 mb-6">
            As a Super Admin, you need to select a company to manage its data sources.
          </p>
          <button onClick={() => navigate('/admin/companies')} className="btn-primary">
            <BuildingOffice2Icon className="w-5 h-5 mr-2" />
            Go to Companies
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Data Sources
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Connect and manage your data
          </p>
        </div>
        <Link to="/data/new" className="btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Data Source
        </Link>
      </div>
      
      {/* Recently used or Quick actions */}
      {(() => {
        const recentTypes = getRecentlyUsedTypes(dataSources);
        
        if (recentTypes.length > 0) {
          // Show recently used types
          return (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon className="w-4 h-4 text-surface-400" />
                <h2 className="text-sm font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wide">
                  Recently Used
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {recentTypes.map(({ type, count }) => {
                  const config = SOURCE_TYPE_CONFIG[type];
                  if (!config) return null;
                  const Icon = config.icon;
                  
                  return (
                    <button
                      key={type}
                      onClick={() => navigate(`/data/new?type=${type}`)}
                      className="card p-4 text-left hover:border-primary-500 transition-colors group"
                    >
                      <div className="flex items-start justify-between">
                        <Icon className={`w-8 h-8 ${config.color} mb-2`} />
                        <span className="text-xs text-surface-400 bg-surface-100 dark:bg-surface-800 px-2 py-0.5 rounded-full">
                          {count} source{count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <h3 className="font-medium text-surface-900 dark:text-white">{config.name}</h3>
                      <p className="text-sm text-surface-500">{config.description}</p>
                    </button>
                  );
                })}
                {/* Fill remaining slots with empty space or show Add button if less than 4 */}
                {recentTypes.length < 4 && (
                  <button
                    onClick={() => navigate('/data/new')}
                    className="card p-4 text-left border-dashed hover:border-primary-500 transition-colors group"
                  >
                    <PlusIcon className="w-8 h-8 text-surface-400 group-hover:text-primary-500 mb-2 transition-colors" />
                    <h3 className="font-medium text-surface-600 dark:text-surface-300 group-hover:text-primary-500 transition-colors">
                      Other Sources
                    </h3>
                    <p className="text-sm text-surface-500">Browse all options</p>
                  </button>
                )}
              </div>
            </div>
          );
        }
        
        // No recent data sources - show default quick actions
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <button
              onClick={() => navigate('/data/new?type=csv')}
              className="card p-4 text-left hover:border-primary-500 transition-colors"
            >
              <DocumentTextIcon className="w-8 h-8 text-primary-500 mb-2" />
              <h3 className="font-medium text-surface-900 dark:text-white">CSV File</h3>
              <p className="text-sm text-surface-500">Upload a CSV file</p>
            </button>
            
            <button
              onClick={() => navigate('/data/new?type=excel')}
              className="card p-4 text-left hover:border-primary-500 transition-colors"
            >
              <TableCellsIcon className="w-8 h-8 text-green-500 mb-2" />
              <h3 className="font-medium text-surface-900 dark:text-white">Excel File</h3>
              <p className="text-sm text-surface-500">Upload an Excel file</p>
            </button>
            
            <button
              onClick={() => navigate('/data/new?type=api')}
              className="card p-4 text-left hover:border-primary-500 transition-colors"
            >
              <GlobeAltIcon className="w-8 h-8 text-amber-500 mb-2" />
              <h3 className="font-medium text-surface-900 dark:text-white">REST API</h3>
              <p className="text-sm text-surface-500">Connect to an API</p>
            </button>
            
            <button
              onClick={() => navigate('/data/new')}
              className="card p-4 text-left hover:border-primary-500 transition-colors"
            >
              <CircleStackIcon className="w-8 h-8 text-emerald-500 mb-2" />
              <h3 className="font-medium text-surface-900 dark:text-white">Database</h3>
              <p className="text-sm text-surface-500">PostgreSQL, MySQL, MongoDB</p>
            </button>
          </div>
        );
      })()}
      
      {/* Data sources list */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="card p-6">
              <div className="flex items-center gap-4">
                <div className="skeleton w-12 h-12 rounded-xl" />
                <div className="flex-1">
                  <div className="skeleton h-5 w-48 mb-2" />
                  <div className="skeleton h-4 w-32" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : dataSources.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center">
            <CircleStackIcon className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            No data sources yet
          </h3>
          <p className="text-surface-500 dark:text-surface-400 mb-6">
            Import your first data source to start building dashboards
          </p>
          <Link to="/data/new" className="btn-primary">
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Data Source
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {dataSources.map(source => {
            const Icon = sourceTypeIcons[source.type] || CircleStackIcon;
            
            return (
              <Link
                key={source._id}
                to={`/data/${source._id}`}
                className="card p-6 block hover:border-primary-500 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-surface-100 dark:bg-surface-800 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-surface-600 dark:text-surface-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-surface-900 dark:text-white truncate">
                        {source.name}
                      </h3>
                      <span className={statusColors[source.status]}>
                        {source.status}
                      </span>
                    </div>
                    <p className="text-sm text-surface-500 dark:text-surface-400">
                      {source.type.toUpperCase()} • {source.stats?.totalRows?.toLocaleString() || 0} rows
                    </p>
                  </div>
                  
                  <div className="text-right text-sm text-surface-400">
                    <p>Last imported</p>
                    <p className="text-surface-600 dark:text-surface-300">
                      {source.stats?.lastImportedAt 
                        ? new Date(source.stats.lastImportedAt).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

